import * as React from 'react';
import { ShieldOff } from 'lucide-react';
import { cn } from '../../lib/cn';

export interface InlineAccessDeniedProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'title'
> {
  /** Short label naming what's restricted (e.g. "Access restricted"). Keep
   *  this inline-scoped -- never reuse a full-page "you don't have access to
   *  this page" title here, since this component renders inside a page (a
   *  table cell, a list body) that already mounted successfully. */
  title: React.ReactNode;
  /** What the caller should do next (e.g. "Ask your firm to enable this
   *  permission for your account."). */
  description: React.ReactNode;
}

/**
 * Compact, inline access-denied message for a spot that already rendered
 * successfully but has a permission-gated data query underneath it -- a
 * table cell, a list body, a tab panel. This is distinct from a full-page
 * access-denied guard (`RequirePermission` in `apps/ledger-web`), which owns
 * the "you can't be on this page at all" case.
 *
 * Always neutral ink/ink-muted colours, never the danger/warning tokens: a
 * permission boundary is not a fault. Using a fault colour here either
 * erodes trust in real warnings elsewhere or falsely implies something is
 * broken (docs/design-system/CLAUDE.md, "Semantic colour tokens used
 * without the condition they signal").
 *
 * This is the ONE canonical component for this rendering -- every call site
 * that needs to say "you can see this list/section exists, but you don't
 * have permission to view its contents" should use this, not a bespoke
 * `<EmptyState>` reuse or a hand-rolled `<p className="text-error">`
 * (docs/design-system/CLAUDE.md, "One canonical component per status/badge
 * concept").
 */
export const InlineAccessDenied = React.forwardRef<
  HTMLDivElement,
  InlineAccessDeniedProps
>(({ title, description, className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 py-8 px-6 text-center',
        className,
      )}
      {...props}
    >
      <ShieldOff
        className="h-6 w-6 text-[var(--ink-faint)]"
        aria-hidden="true"
      />
      <div className="text-[13px] font-semibold text-ink">{title}</div>
      <div className="text-[13px] text-ink-muted max-w-sm">{description}</div>
    </div>
  );
});
InlineAccessDenied.displayName = 'InlineAccessDenied';
